/**
 * Notification Service
 *
 * Handles form submissions and email notifications.
 * - Stores all submissions in Supabase
 * - Sends email notifications via Supabase Edge Function
 *
 * Usage:
 *   import { notificationService } from '@/services/notificationService';
 *   await notificationService.submitContactForm(data);
 */

import { supabase, isDemoMode } from '@/lib/supabase';
import type {
  ContactFormData,
  SignupData,
  DemoRequestData,
  WorkOrderNotificationData,
  NotificationResult,
  NotificationType,
} from '@/types/notifications';

// Admin emails - notifications will be sent to both
const ADMIN_EMAILS = ['getaxisplatform@gmail.com', 'ct.lilley19@gmail.com'];

// Type helper for tables not yet in database schema
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyTable = any;

class NotificationService {
  private inDemoMode: boolean;

  constructor() {
    // Check if we're in demo mode (no real Supabase connection)
    this.inDemoMode = isDemoMode();
  }

  /**
   * Submit contact form and notify admin
   */
  async submitContactForm(data: ContactFormData): Promise<NotificationResult> {
    try {
      // Store in database
      const submission = {
        name: data.name,
        email: data.email,
        company: data.company || null,
        phone: data.phone || null,
        property_count: data.propertyCount || null,
        interest_type: data.interestType || null,
        message: data.message || null,
        source: data.source || 'landing_page',
        status: 'new',
      };

      if (this.inDemoMode) {
        console.log('[Demo Mode] Contact form submission:', submission);
        return { success: true, id: 'demo-' + Date.now() };
      }

      const { data: inserted, error: dbError } = await (supabase
        .from('contact_submissions') as AnyTable)
        .insert(submission)
        .select()
        .single();

      if (dbError) {
        console.error('Failed to store contact submission:', dbError);
        throw new Error('Failed to save submission');
      }

      // Send email notification
      await this.sendEmailNotification({
        type: 'contact_form',
        referenceId: inserted?.id,
        data: {
          name: data.name,
          email: data.email,
          company: data.company,
          propertyCount: data.propertyCount,
          interestType: data.interestType,
          message: data.message,
        },
      });

      return { success: true, id: inserted?.id };
    } catch (error) {
      console.error('Contact form submission error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Record user signup and notify admin
   */
  async recordSignup(data: SignupData): Promise<NotificationResult> {
    try {
      const signup = {
        full_name: data.fullName,
        email: data.email,
        organization_name: data.organizationName || null,
        property_type: data.propertyType || null,
        estimated_units: data.estimatedUnits || null,
        team_size: data.teamSize || null,
        selected_plan: data.selectedPlan || null,
        billing_interval: data.billingInterval || null,
        referral_code: data.referralCode || null,
        utm_source: data.utmSource || null,
        utm_medium: data.utmMedium || null,
        utm_campaign: data.utmCampaign || null,
        source: 'website',
      };

      if (this.inDemoMode) {
        console.log('[Demo Mode] User signup:', signup);
        return { success: true, id: 'demo-' + Date.now() };
      }

      const { data: inserted, error: dbError } = await (supabase
        .from('user_signups') as AnyTable)
        .insert(signup)
        .select()
        .single();

      if (dbError) {
        console.error('Failed to store signup:', dbError);
        throw new Error('Failed to save signup');
      }

      // Send email notification
      await this.sendEmailNotification({
        type: 'signup',
        referenceId: inserted?.id,
        data: {
          fullName: data.fullName,
          email: data.email,
          organizationName: data.organizationName,
          propertyType: data.propertyType,
          estimatedUnits: data.estimatedUnits,
          selectedPlan: data.selectedPlan,
        },
      });

      return { success: true, id: inserted?.id };
    } catch (error) {
      console.error('Signup recording error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Submit demo/pricing request and notify admin
   */
  async submitDemoRequest(data: DemoRequestData): Promise<NotificationResult> {
    try {
      const request = {
        name: data.name,
        email: data.email,
        company: data.company || null,
        phone: data.phone || null,
        request_type: data.requestType,
        property_count: data.propertyCount || null,
        current_solution: data.currentSolution || null,
        timeline: data.timeline || null,
        message: data.message || null,
        status: 'pending',
      };

      if (this.inDemoMode) {
        console.log('[Demo Mode] Demo request:', request);
        return { success: true, id: 'demo-' + Date.now() };
      }

      const { data: inserted, error: dbError } = await (supabase
        .from('demo_requests') as AnyTable)
        .insert(request)
        .select()
        .single();

      if (dbError) {
        console.error('Failed to store demo request:', dbError);
        throw new Error('Failed to save request');
      }

      // Send email notification
      await this.sendEmailNotification({
        type: 'demo_request',
        referenceId: inserted?.id,
        data: {
          name: data.name,
          email: data.email,
          company: data.company,
          requestType: data.requestType,
          propertyCount: data.propertyCount,
          timeline: data.timeline,
          message: data.message,
        },
      });

      return { success: true, id: inserted?.id };
    } catch (error) {
      console.error('Demo request submission error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Notify admin about new work order (for future use)
   */
  async notifyWorkOrderCreated(data: WorkOrderNotificationData): Promise<NotificationResult> {
    try {
      if (this.inDemoMode) {
        console.log('[Demo Mode] Work order notification:', data);
        return { success: true };
      }

      await this.sendEmailNotification({
        type: 'work_order_created',
        referenceId: data.workOrderId,
        data: data as unknown as Record<string, unknown>,
      });

      return { success: true };
    } catch (error) {
      console.error('Work order notification error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Send email notification via Supabase Edge Function
   */
  private async sendEmailNotification(params: {
    type: NotificationType;
    referenceId?: string;
    data: Record<string, unknown>;
  }): Promise<void> {
    // Send to all admin emails
    for (const email of ADMIN_EMAILS) {
      try {
        const { data, error } = await supabase.functions.invoke('send-notification', {
          body: {
            type: params.type,
            referenceId: params.referenceId,
            recipientEmail: email,
            data: params.data,
          },
        });

        if (error) {
          console.error(`Email notification failed for ${email}:`, error);
          // Don't throw - we don't want to fail the whole submission if email fails
          // The data is already stored in the database
        } else {
          console.log(`Email notification sent to ${email}:`, data);
        }
      } catch (error) {
        console.error(`Email notification error for ${email}:`, error);
        // Don't throw - email failure shouldn't break the submission
      }
    }
  }

  /**
   * Log notification to database
   */
  async logNotification(params: {
    type: NotificationType;
    referenceId?: string;
    referenceTable?: string;
    recipientEmail: string;
    subject: string;
    status: 'sent' | 'delivered' | 'failed' | 'bounced';
    resendId?: string;
    errorMessage?: string;
  }): Promise<void> {
    if (this.inDemoMode) {
      console.log('[Demo Mode] Notification log:', params);
      return;
    }

    try {
      await (supabase.from('notification_log') as AnyTable).insert({
        notification_type: params.type,
        reference_id: params.referenceId || null,
        reference_table: params.referenceTable || null,
        recipient_email: params.recipientEmail,
        subject: params.subject,
        status: params.status,
        resend_id: params.resendId || null,
        error_message: params.errorMessage || null,
      });
    } catch (error) {
      console.error('Failed to log notification:', error);
    }
  }
}

export const notificationService = new NotificationService();
